import { formatDate as dateFnsFormatDate } from 'date-fns';
import { Users } from 'lucide-react';
import { Calendar } from 'lucide-react';
import { ThumbsDown } from 'lucide-react';
import { ThumbsUp } from 'lucide-react';
import { MessageSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogDescription,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { MatchFeedback } from './AdminUserMatches';

export default function AdminFeebackDialog({
  feedbackOpen,
  setFeedbackOpen,
  selectedFeedback,
  currentUserName,
  otherUserName,
  currentUserId,
  otherUserId,
}: {
  feedbackOpen: boolean;
  setFeedbackOpen: (open: boolean) => void;
  selectedFeedback: MatchFeedback[];
  currentUserName: string;
  otherUserName: string;
  currentUserId: string;
  otherUserId: string;
}) {
  const formatDate = (date: Date) => dateFnsFormatDate(date, 'MMM d, yyyy');

  return (
    <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Feedback</DialogTitle>
          <DialogDescription>
            {selectedFeedback.length > 0
              ? `${selectedFeedback.length} feedback entries collected from this match`
              : 'No feedback collected yet'}
          </DialogDescription>
        </DialogHeader>

        {selectedFeedback.length > 0 ? (
          <div className='mt-2'>
            {selectedFeedback.length > 1 ? (
              <Tabs defaultValue='latest' className='w-full'>
                <TabsList className='mb-4 grid grid-cols-2'>
                  <TabsTrigger value='latest'>Latest</TabsTrigger>
                  <TabsTrigger value='all'>All Feedback</TabsTrigger>
                </TabsList>

                <TabsContent value='latest'>
                  <FeedbackEntry
                    feedback={
                      selectedFeedback.sort(
                        (a, b) =>
                          new Date(b.createdDate).getTime() -
                          new Date(a.createdDate).getTime()
                      )[0]
                    }
                    formatDate={formatDate}
                    currentUserId={currentUserId}
                    otherUserId={otherUserId}
                    currentUserName={currentUserName}
                    otherUserName={otherUserName}
                  />
                </TabsContent>

                <TabsContent value='all'>
                  <div className='max-h-[60vh] space-y-6 overflow-y-auto pr-2'>
                    {[...selectedFeedback]
                      .sort(
                        (a, b) =>
                          new Date(b.createdDate).getTime() -
                          new Date(a.createdDate).getTime()
                      )
                      .map((feedback, index) => (
                        <div
                          key={feedback.id as string}
                          className={index > 0 ? 'border-t pt-4' : ''}
                        >
                          <FeedbackEntry
                            feedback={feedback}
                            formatDate={formatDate}
                            currentUserId={currentUserId}
                            otherUserId={otherUserId}
                            currentUserName={currentUserName}
                            otherUserName={otherUserName}
                          />
                        </div>
                      ))}
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <FeedbackEntry
                feedback={selectedFeedback[0]}
                formatDate={formatDate}
                currentUserId={currentUserId}
                otherUserId={otherUserId}
                currentUserName={currentUserName}
                otherUserName={otherUserName}
              />
            )}
          </div>
        ) : (
          <div className='text-muted-foreground py-6 text-center'>
            No feedback has been collected for this match yet.
          </div>
        )}

        <div className='flex justify-end'>
          <DialogClose asChild>
            <Button variant='outline'>Close</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackEntry({
  feedback,
  formatDate,
  currentUserId,
  otherUserId,
  currentUserName,
  otherUserName,
}: {
  feedback: MatchFeedback;
  formatDate: (date: Date) => string;
  currentUserId: string;
  otherUserId: string;
  currentUserName: string;
  otherUserName: string;
}) {
  return (
    <div className='space-y-4'>
      <h2 className='font-semibold'>
        Feedback from{' '}
        {feedback.userId === currentUserId
          ? currentUserName
          : feedback.userId === otherUserId
            ? otherUserName
            : 'Unknown'}
      </h2>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          {(feedback.positiveExperience === true ||
            (feedback.positiveExperience === null && feedback.explanation)) && (
            <Badge
              className={
                feedback.positiveExperience === true
                  ? 'bg-green-500'
                  : 'bg-red-500'
              }
            >
              {feedback.positiveExperience === true ? (
                <>
                  <ThumbsUp className='mr-1 h-3 w-3' /> Positive Exp.
                </>
              ) : (
                <>
                  <ThumbsDown className='mr-1 h-3 w-3' /> Negative Exp.
                </>
              )}
            </Badge>
          )}

          <Badge
            variant='outline'
            className={
              feedback.weMet
                ? 'border-green-600 text-green-600'
                : 'border-blue-600 text-blue-600'
            }
          >
            {feedback.weMet ? (
              <>
                <Users className='mr-1 h-3 w-3' /> Met
              </>
            ) : (
              <>
                <Users className='mr-1 h-3 w-3' /> Did not meet
              </>
            )}
          </Badge>
        </div>

        <div className='text-muted-foreground flex items-center text-xs'>
          <Calendar className='mr-1 h-3 w-3' />
          {formatDate(new Date(feedback.createdDate))}
        </div>
      </div>

      {feedback.explanation && (
        <div className='bg-muted rounded-md p-4'>
          <h4 className='mb-2 text-sm font-medium'>Explanation:</h4>
          <p className='text-muted-foreground text-sm'>
            {feedback.explanation}
          </p>
        </div>
      )}
    </div>
  );
}

export function FeedbackButton({
  matchCount,
  onClick,
}: {
  matchCount: number;
  onClick: () => void;
}) {
  if (matchCount === 0) return null;

  return (
    <Button
      variant='ghost'
      size='icon'
      title='View Feedback'
      onClick={onClick}
      className='relative'
    >
      <MessageSquare className='h-4 w-4' />
      <span className='bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-xs'>
        {matchCount}
      </span>
    </Button>
  );
}
